import { Children, cloneElement, isValidElement, useEffect, useId, useState } from "react";
import { useParams } from "react-router-dom";
import WorkspaceComplianceFrame from "../../components/workspaceCompliance/WorkspaceComplianceFrame";
import { SectionCard } from "../../components/workspaceCompliance/WorkspaceComplianceUi";
import InfoTooltip from "../../components/common/InfoTooltip";
import {
  createWorkspaceFollowUpCase,
  createWorkspaceHostSupportEvent,
  getMyOperatorAffiliations,
  getWorkspaceHostSupportDashboard,
  updateWorkspaceFollowUpCaseStatus,
} from "../../api/workspaceSupport";
import { useAuth } from "../../context/AuthContext";
import useOperatorWorkspace from "../../hooks/useOperatorWorkspace";
import { titleCaseComplianceLabel } from "../../constants/workspaceCompliance";
import { getAdvancedRecordTooltip } from "../../components/workspaceCompliance/advancedRecordsHelpContent";

const defaultEventForm = {
  client_id: "",
  operator_user_id: "",
  service_started_at: "",
  service_ended_at: "",
  service_category: "",
  service_name: "",
  amount: "",
  follow_up_model: "provider_only",
  warranty_eligible: false,
  warranty_expires_at: "",
  backup_provider_user_id: "",
  backup_provider_authorized: false,
  contractor_contact_route: "",
  support_notes: "",
};

const defaultCaseForm = {
  service_event_id: "",
  follow_up_model: "provider_only",
  subject: "",
  description: "",
  assigned_backup_provider_user_id: "",
};

function toIsoOrNull(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

function Field({ label, children, tooltip = "" }) {
  const generatedId = useId();
  const child =
    Children.count(children) === 1 && isValidElement(children)
      ? Children.only(children)
      : null;
  const controlId = child ? child.props.id || `workspace-support-field-${generatedId}` : undefined;
  const renderedChildren = child ? cloneElement(child, { id: controlId }) : children;

  return (
    <div className="min-w-0 space-y-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <label htmlFor={controlId} className="text-sm font-semibold text-slate-700">
          {label}
        </label>
        {tooltip ? (
          <InfoTooltip
            text={tooltip}
            label={label}
            ariaLabel="More information"
            placement="bottom"
            maxWidth={300}
          />
        ) : null}
      </div>
      {renderedChildren}
    </div>
  );
}

function StatCard({ label, value, detail, tooltip = "" }) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        <span>{label}</span>
        {tooltip ? (
          <InfoTooltip
            text={tooltip}
            label={label}
            ariaLabel="More information"
            placement="bottom"
            maxWidth={300}
          />
        ) : null}
      </div>
      <div className="mt-2 text-xl font-semibold text-slate-900">{value}</div>
      {detail ? <div className="mt-1 text-sm text-slate-500">{detail}</div> : null}
    </article>
  );
}

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatAmount(amount, currency = "usd") {
  if (amount === null || amount === undefined || amount === "") return "Not recorded";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
}

const supportValueLabels = {
  provider_only: "Renter only",
  host_assisted_triage: "Salon-assisted triage",
  backup_provider_authorized: "Backup professional authorized",
  host_supported_per_policy: "Salon-supported per policy",
  backup_provider_requested: "Backup professional requested",
  backup_provider_engaged: "Backup professional engaged",
  responded: "Responded",
};

function formatSupportLabel(value) {
  if (!value) return "Not recorded";
  return supportValueLabels[value] || titleCaseComplianceLabel(value);
}

export default function OperatorWorkspaceSupportPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const { workspace, error: workspaceError } = useOperatorWorkspace(id);
  const [dashboard, setDashboard] = useState({
    service_events: [],
    follow_up_cases: [],
  });
  const [affiliations, setAffiliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingEvent, setSavingEvent] = useState(false);
  const [savingCase, setSavingCase] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [eventForm, setEventForm] = useState(defaultEventForm);
  const [caseForm, setCaseForm] = useState(defaultCaseForm);

  const loadData = async () => {
    if (!token || !id) return;
    setLoading(true);
    setError("");
    try {
      const [dashboardData, affiliationData] = await Promise.all([
        getWorkspaceHostSupportDashboard(id, token),
        getMyOperatorAffiliations(token).catch(() => []),
      ]);
      setDashboard({
        service_events: Array.isArray(dashboardData?.service_events)
          ? dashboardData.service_events
          : [],
        follow_up_cases: Array.isArray(dashboardData?.follow_up_cases)
          ? dashboardData.follow_up_cases
          : [],
      });
      setAffiliations(Array.isArray(affiliationData) ? affiliationData : []);
    } catch (err) {
      setError(err.message || "Unable to load workspace support data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id, token]);

  const serviceEvents = dashboard.service_events || [];
  const followUpCases = dashboard.follow_up_cases || [];
  const openCases = followUpCases.filter((item) => item.status !== "closed" && item.status !== "resolved");

  const handleCreateEvent = async (event) => {
    event.preventDefault();
    if (!token || !id) return;
    setSavingEvent(true);
    setError("");
    setFlash("");
    try {
      await createWorkspaceHostSupportEvent(
        id,
        {
          client_id: Number(eventForm.client_id),
          owner_type: "independent_operator",
          owner_operator_user_id: eventForm.operator_user_id
            ? Number(eventForm.operator_user_id)
            : null,
          operator_user_id: eventForm.operator_user_id
            ? Number(eventForm.operator_user_id)
            : null,
          service_started_at: toIsoOrNull(eventForm.service_started_at),
          service_ended_at: toIsoOrNull(eventForm.service_ended_at),
          service_category: eventForm.service_category || null,
          service_name: eventForm.service_name || null,
          amount: eventForm.amount ? Number(eventForm.amount) : null,
          follow_up_model: eventForm.follow_up_model,
          warranty_eligible: Boolean(eventForm.warranty_eligible),
          warranty_expires_at: toIsoOrNull(eventForm.warranty_expires_at),
          backup_provider_user_id: eventForm.backup_provider_user_id
            ? Number(eventForm.backup_provider_user_id)
            : null,
          backup_provider_authorized: Boolean(eventForm.backup_provider_authorized),
          contractor_contact_route: eventForm.contractor_contact_route || null,
          support_notes: eventForm.support_notes || null,
        },
        token
      );
      setEventForm(defaultEventForm);
      setFlash("Service event recorded with workspace support visibility.");
      await loadData();
    } catch (err) {
      setError(err.message || "Unable to record the service event.");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleCreateCase = async (event) => {
    event.preventDefault();
    if (!token || !id) return;
    setSavingCase(true);
    setError("");
    setFlash("");
    try {
      await createWorkspaceFollowUpCase(
        id,
        {
          service_event_id: Number(caseForm.service_event_id),
          follow_up_model: caseForm.follow_up_model,
          subject: caseForm.subject,
          description: caseForm.description || null,
          assigned_backup_provider_user_id: caseForm.assigned_backup_provider_user_id
            ? Number(caseForm.assigned_backup_provider_user_id)
            : null,
        },
        token
      );
      setCaseForm(defaultCaseForm);
      setFlash("Follow-up case opened.");
      await loadData();
    } catch (err) {
      setError(err.message || "Unable to open the follow-up case.");
    } finally {
      setSavingCase(false);
    }
  };

  const handleCaseStatus = async (caseId, status, overrides = {}) => {
    if (!token || !id) return;
    setError("");
    setFlash("");
    try {
      await updateWorkspaceFollowUpCaseStatus(
        id,
        caseId,
        {
          status,
          resolution_summary:
            status === "resolved" || status === "closed"
              ? "Closed from the workspace support dashboard."
              : null,
          ...overrides,
        },
        token
      );
      setFlash(`Follow-up case marked ${titleCaseComplianceLabel(status).toLowerCase()}.`);
      await loadData();
    } catch (err) {
      setError(err.message || "Unable to update the follow-up case.");
    }
  };

  return (
    <WorkspaceComplianceFrame
      workspace={workspace}
      workspaceId={id}
      title="Support & Follow-up"
      description="Track service events, warranty follow-up, and renter support records for an active rental workspace."
    >
      {workspaceError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {workspaceError}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {flash ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash}
        </div>
      ) : null}

      <section className="grid gap-4 md:grid-cols-4">
        <StatCard label="Service events" value={serviceEvents.length} detail="Workspace support records" tooltip="Recorded support or warranty service events for this workspace." />
        <StatCard label="Open cases" value={openCases.length} detail="Complaint or follow-up triage" tooltip="Follow-up cases that still need action." />
        <StatCard
          label="Warranty eligible"
          value={serviceEvents.filter((item) => item.warranty_eligible).length}
          detail="Visits with a recorded warranty window"
          tooltip="Service events marked as eligible for warranty follow-up."
        />
        <StatCard
          label="Affiliations"
          value={affiliations.length}
          detail="Renter-to-salon workspace relationships"
          tooltip="Independent professional relationships connected to this workspace."
        />
      </section>

      <SectionCard
        title="Support Visibility"
        description="This dashboard is intentionally narrower than full client ownership. The salon can see the client contact details needed for service recovery, the renter involved, service timing, follow-up responsibility, warranty status, and case status for work performed at this location."
        tooltip="Explains what the salon can and cannot see in support records."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
            <h3 className="text-lg font-semibold text-slate-900">Salon can see</h3>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
              <li>Client name and contact details needed for service recovery</li>
              <li>Renter, service timing, category, and amount band</li>
              <li>Warranty status, follow-up model, backup coverage, and case status</li>
            </ul>
          </div>
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
            <h3 className="text-lg font-semibold text-amber-900">Salon cannot do by default</h3>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-amber-800">
              <li>Take over the renter's client book</li>
              <li>Export or market to renter-owned clients</li>
              <li>Browse unrelated history from other salon locations</li>
            </ul>
          </div>
        </div>
      </SectionCard>

      <section className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
        <SectionCard
          title="Service Events"
          description="Record renter-serviced visits so the salon can triage complaints, warranty work, and operational recovery without changing client ownership."
          tooltip="Saved support or service records for this workspace."
        >
          {loading ? <p className="text-sm text-slate-600">Loading support dashboard...</p> : null}
          <div className="space-y-4">
            {serviceEvents.length ? (
              serviceEvents.map((item) => (
                <article
                  key={item.id}
                  className="rounded-3xl border border-slate-200 bg-slate-50 p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold text-slate-900">{item.client_name}</div>
                      <div className="mt-1 text-sm text-slate-600">
                        {item.service_name || item.service_category || "Service event"} with{" "}
                        {item.operator_name || "Unassigned renter"}
                      </div>
                    </div>
                    <div className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                      {formatSupportLabel(item.follow_up_model)}
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Service at {formatDateTime(item.service_started_at)}
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      {formatAmount(item.amount, item.currency)}
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      {item.warranty_eligible
                        ? `Warranty until ${formatDateTime(item.warranty_expires_at)}`
                        : "No warranty recorded"}
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Contact: {item.client_contact_email || item.client_contact_phone || "Not recorded"}
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Renter contact: {item.contractor_contact_route || "Not recorded"}
                    </div>
                  </div>
                  {item.support_notes ? (
                    <p className="mt-4 text-sm leading-6 text-slate-600">{item.support_notes}</p>
                  ) : null}
                </article>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-sm text-slate-600">
                No service events have been recorded yet for this workspace.
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard
          title="Record Service Event"
          description="Enter only the operational details the salon needs for follow-up support at this location."
          tooltip="Use this form to record a service or support event that may need follow-up."
        >
          <form className="space-y-4" onSubmit={handleCreateEvent}>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Client ID" tooltip={getAdvancedRecordTooltip("Client ID")}>
                <input
                  required
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.client_id}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, client_id: event.target.value }))
                  }
                />
              </Field>
              <Field label="Renter user ID" tooltip={getAdvancedRecordTooltip("Renter user ID")}>
                <input
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.operator_user_id}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, operator_user_id: event.target.value }))
                  }
                />
              </Field>
              <Field label="Service started" tooltip={getAdvancedRecordTooltip("Service started")}>
                <input
                  required
                  type="datetime-local"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.service_started_at}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, service_started_at: event.target.value }))
                  }
                />
              </Field>
              <Field label="Service ended" tooltip={getAdvancedRecordTooltip("Service ended")}>
                <input
                  type="datetime-local"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.service_ended_at}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, service_ended_at: event.target.value }))
                  }
                />
              </Field>
              <Field label="Service category" tooltip={getAdvancedRecordTooltip("Service category")}>
                <input
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.service_category}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, service_category: event.target.value }))
                  }
                />
              </Field>
              <Field label="Service name" tooltip={getAdvancedRecordTooltip("Service name")}>
                <input
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.service_name}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, service_name: event.target.value }))
                  }
                />
              </Field>
              <Field label="Amount" tooltip={getAdvancedRecordTooltip("Amount")}>
                <input
                  type="number"
                  step="0.01"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.amount}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, amount: event.target.value }))
                  }
                />
              </Field>
              <Field label="Follow-up model" tooltip={getAdvancedRecordTooltip("Follow-up model")}>
                <select
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.follow_up_model}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, follow_up_model: event.target.value }))
                  }
                >
                  <option value="provider_only">Renter only</option>
                  <option value="host_assisted_triage">Salon-assisted triage</option>
                  <option value="backup_provider_authorized">Backup professional authorized</option>
                  <option value="host_supported_per_policy">Salon-supported per policy</option>
                </select>
              </Field>
              <Field label="Warranty expires" tooltip={getAdvancedRecordTooltip("Warranty expires")}>
                <input
                  type="datetime-local"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.warranty_expires_at}
                  onChange={(event) =>
                    setEventForm((prev) => ({ ...prev, warranty_expires_at: event.target.value }))
                  }
                />
              </Field>
              <Field label="Backup professional user ID" tooltip={getAdvancedRecordTooltip("Backup professional user ID")}>
                <input
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.backup_provider_user_id}
                  onChange={(event) =>
                    setEventForm((prev) => ({
                      ...prev,
                      backup_provider_user_id: event.target.value,
                    }))
                  }
                />
              </Field>
              <Field label="Renter contact route" tooltip={getAdvancedRecordTooltip("Renter contact route")}>
                <input
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={eventForm.contractor_contact_route}
                  onChange={(event) =>
                    setEventForm((prev) => ({
                      ...prev,
                      contractor_contact_route: event.target.value,
                    }))
                  }
                />
              </Field>
            </div>

            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={eventForm.warranty_eligible}
                onChange={(event) =>
                  setEventForm((prev) => ({ ...prev, warranty_eligible: event.target.checked }))
                }
              />
              Warranty may still apply
            </label>
            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={eventForm.backup_provider_authorized}
                onChange={(event) =>
                  setEventForm((prev) => ({
                    ...prev,
                    backup_provider_authorized: event.target.checked,
                  }))
                }
              />
              Backup professional is authorized for follow-up
            </label>
            <Field label="Support notes" tooltip={getAdvancedRecordTooltip("Support notes")}>
              <textarea
                rows={4}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900"
                value={eventForm.support_notes}
                onChange={(event) =>
                  setEventForm((prev) => ({ ...prev, support_notes: event.target.value }))
                }
              />
            </Field>
            <button
              type="submit"
              disabled={savingEvent}
              className="rounded-full bg-rose-500 px-5 py-3 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingEvent ? "Saving..." : "Record Service Event"}
            </button>
          </form>
        </SectionCard>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
        <SectionCard
          title="Follow-Up Cases"
          description="Open and close complaint or warranty cases without changing renter client ownership."
          tooltip="Cases are unresolved support items that need attention after a service event."
        >
          <div className="space-y-4">
            {followUpCases.length ? (
              followUpCases.map((item) => (
                <article key={item.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold text-slate-900">{item.subject}</div>
                      <div className="mt-1 text-sm text-slate-600">
                        {item.client_name} with {item.operator_name || "Unassigned renter"}
                      </div>
                    </div>
                    <div className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                      {titleCaseComplianceLabel(item.status)}
                    </div>
                  </div>
                  {item.description ? (
                    <p className="mt-4 text-sm leading-6 text-slate-600">{item.description}</p>
                  ) : null}
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Opened {formatDateTime(item.opened_at)}
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Model {formatSupportLabel(item.follow_up_model)}
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Renter response: {formatSupportLabel(item.contractor_response_sla_status)}
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Salon escalation: {formatSupportLabel(item.host_escalation_state)}
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                      Backup professional: {item.backup_provider_escalation_authorized ? "Authorized" : "Not authorized"}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {item.contractor_response_sla_status !== "responded" &&
                    item.status !== "resolved" &&
                    item.status !== "closed" ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleCaseStatus(item.id, item.status, {
                            mark_contractor_responded: true,
                          })
                        }
                        className="rounded-full border border-sky-300 px-4 py-2 text-sm font-semibold text-sky-700 hover:border-sky-400"
                      >
                        Mark Renter Responded
                      </button>
                    ) : null}
                    {item.host_escalation_state !== "backup_provider_engaged" &&
                    item.status !== "resolved" &&
                    item.status !== "closed" ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleCaseStatus(item.id, item.status, {
                            host_escalation_state: "backup_provider_requested",
                            backup_provider_escalation_authorized: true,
                            mark_backup_provider_engaged: true,
                          })
                        }
                        className="rounded-full border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-700 hover:border-amber-400"
                      >
                        Request Backup Professional
                      </button>
                    ) : null}
                    {item.status !== "in_progress" ? (
                      <button
                        type="button"
                        onClick={() => handleCaseStatus(item.id, "in_progress")}
                        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
                      >
                        Mark In Progress
                      </button>
                    ) : null}
                    {item.status !== "resolved" ? (
                      <button
                        type="button"
                        onClick={() => handleCaseStatus(item.id, "resolved")}
                        className="rounded-full border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 hover:border-emerald-400"
                      >
                        Resolve Case
                      </button>
                    ) : null}
                    {item.status !== "closed" ? (
                      <button
                        type="button"
                        onClick={() => handleCaseStatus(item.id, "closed")}
                        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
                      >
                        Close Case
                      </button>
                    ) : null}
                  </div>
                </article>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-sm text-slate-600">
                No follow-up cases are open for this workspace.
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard
          title="Open Follow-Up Case"
          description="Tie the case to a recorded service event so the salon can confirm the visit occurred at this location and see warranty context."
          tooltip="Use this form when a support item still needs follow-up."
        >
          <form className="space-y-4" onSubmit={handleCreateCase}>
            <Field label="Service event" tooltip={getAdvancedRecordTooltip("Service event")}>
              <select
                required
                className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                value={caseForm.service_event_id}
                onChange={(event) =>
                  setCaseForm((prev) => ({ ...prev, service_event_id: event.target.value }))
                }
              >
                <option value="">Select a service event</option>
                {serviceEvents.map((item) => (
                  <option key={item.id} value={item.id}>
                    #{item.id} {item.client_name} - {item.service_name || item.service_category || "Service"}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Follow-up model" tooltip={getAdvancedRecordTooltip("Follow-up model")}>
              <select
                className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                value={caseForm.follow_up_model}
                onChange={(event) =>
                  setCaseForm((prev) => ({ ...prev, follow_up_model: event.target.value }))
                }
              >
                <option value="provider_only">Renter only</option>
                <option value="host_assisted_triage">Salon-assisted triage</option>
                <option value="backup_provider_authorized">Backup professional authorized</option>
                <option value="host_supported_per_policy">Salon-supported per policy</option>
              </select>
            </Field>
            <Field label="Subject" tooltip={getAdvancedRecordTooltip("Subject")}>
              <input
                required
                className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                value={caseForm.subject}
                onChange={(event) =>
                  setCaseForm((prev) => ({ ...prev, subject: event.target.value }))
                }
              />
            </Field>
            <Field label="Assigned backup professional user ID" tooltip={getAdvancedRecordTooltip("Assigned backup professional user ID")}>
              <input
                className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                value={caseForm.assigned_backup_provider_user_id}
                onChange={(event) =>
                  setCaseForm((prev) => ({
                    ...prev,
                    assigned_backup_provider_user_id: event.target.value,
                  }))
                }
              />
            </Field>
            <Field label="Description" tooltip={getAdvancedRecordTooltip("Description")}>
              <textarea
                rows={4}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900"
                value={caseForm.description}
                onChange={(event) =>
                  setCaseForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </Field>
            <button
              type="submit"
              disabled={savingCase}
              className="rounded-full bg-rose-500 px-5 py-3 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingCase ? "Opening..." : "Open Follow-Up Case"}
            </button>
          </form>
        </SectionCard>
      </section>

      <SectionCard
        title="Independent Professional Links"
        description="These records show how one independent professional can stay portable across multiple salon locations and workspaces without creating a separate salon identity for each location."
        tooltip="Shows independent professional relationships connected to salon locations and workspaces."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          {affiliations.length ? (
            affiliations.map((item) => (
              <article key={item.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="text-lg font-semibold text-slate-900">
                  {item.business_name || item.host_salon_name || `Affiliation #${item.id}`}
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Salon: {item.host_salon_name || `Salon #${item.host_salon_id}`}
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Workspace: {item.workspace_name || `Workspace #${item.workspace_id || "n/a"}`}
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Status: {titleCaseComplianceLabel(item.status)} / {titleCaseComplianceLabel(item.affiliation_kind)}
                </div>
              </article>
            ))
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-sm text-slate-600">
              No operator affiliations have been recorded yet.
            </div>
          )}
        </div>
      </SectionCard>
    </WorkspaceComplianceFrame>
  );
}
